import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../FirebaseConfig";
import { DateField } from "../components/DateField";
import { LocationCombobox } from "../components/LocationCombobox";
import { WideSelect } from "../components/WideSelect";
import { ConflictModal, type ConflictDetails } from "../components/ConflictModal";
import { SavingOverlay } from "../components/SavingOverlay";
import { ClearableField } from "../components/ClearableField";
import { useAuth } from "../lib/auth-context";
import { useCollection } from "../lib/firestore-hooks";
import { useViewAsUser } from "../lib/view-as";
import {
  dowEs,
  formatFecha,
  formatFechaShort,
  formatHora,
  todaySantiago,
} from "../lib/format";
import { generateTimeGrid } from "../lib/time-grid";
import type { Usuario } from "../lib/auth-context";
import type {
  AeropuertoDoc,
  AvionDoc,
  BlackoutDoc,
  CuentaDoc,
  DistanciaDoc,
  UsuarioDoc,
  VueloDoc,
} from "../lib/types";
import { distanciaId } from "../lib/types";

interface VueloRow extends VueloDoc {
  id: string;
}

interface BlackoutRow extends BlackoutDoc {
  id: string;
  aircraftId: string;
  aircraftDisplayName: string;
}

function tsToIso(t: Timestamp): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(t.toDate());
}

interface FerryLeg {
  direction: "pre" | "post";
  fromIcao: string | null;
  toIcao: string | null;
  approxHora: string | null;
  distanceNm: number | null;
  flightTimeMin: number | null;
}

const FERRY_GROUND_BUFFER_MIN = 30;
const MIN_FERRY_MIN = 30;

function computeFerryMin(
  distanceNm: number | null,
  speedKt: number | null
): number {
  if (!distanceNm || !speedKt || speedKt <= 0) return MIN_FERRY_MIN;
  const raw = Math.ceil((distanceNm / speedKt) * 60);
  return Math.max(MIN_FERRY_MIN, Math.ceil(raw / 15) * 15);
}

function addMinutesHora(hora: string, mins: number): string | null {
  const m = hora.match(/^(\d{2}):(\d{2})/);
  if (!m) return null;
  const h0 = parseInt(m[1]!, 10);
  const m0 = parseInt(m[2]!, 10);
  if (Number.isNaN(h0) || Number.isNaN(m0)) return null;
  const total = h0 * 60 + m0 + mins;
  const norm = ((total % 1440) + 1440) % 1440;
  const hh = Math.floor(norm / 60).toString().padStart(2, "0");
  const mm = (norm % 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

interface FlightBlock {
  flight: VueloRow;
  preFerry: FerryLeg | null;
  postFerry: FerryLeg | null;
}

function flightPostLocation(
  flight: VueloRow,
  homeBaseIcao: string | null
): string | null {
  switch (flight.postFlightDisposition) {
    case "home":
      return homeBaseIcao;
    case "relocate_other":
      return flight.postFlightDestinationIcao;
    case "stay":
    default:
      return flight.destinationIcao;
  }
}

function aircraftLocationBefore(
  flight: VueloRow,
  flights: VueloRow[],
  homeBaseIcao: string | null
): string | null {
  let prev: VueloRow | null = null;
  for (const f of flights) {
    if (f.id === flight.id) break;
    if (f.aircraftId !== flight.aircraftId) continue;
    if (f.status !== "approved" && f.status !== "completed") continue;
    prev = f;
  }
  if (!prev) return homeBaseIcao;
  return flightPostLocation(prev, homeBaseIcao);
}

/** 9-col table frame, same visual treatment as the admin table. */
function frameBorders(
  pos: "alone" | "top" | "middle" | "bottom",
  idx: number,
  lastIdx: number
): string {
  if (pos === "alone") {
    const lCls = idx === 0 ? "border-l border-l-slate-200" : "";
    const rCls = idx === lastIdx ? "border-r border-r-slate-200" : "";
    let rounded = "";
    if (idx === 0) rounded = "rounded-l-md";
    else if (idx === lastIdx) rounded = "rounded-r-md";
    return `border-t border-b border-t-slate-200 border-b-slate-200 ${lCls} ${rCls} ${rounded}`;
  }
  const tCls = pos === "top" ? "border-t-2 border-t-amber-400" : "";
  const bCls = pos === "bottom" ? "border-b-2 border-b-amber-400" : "";
  const lCls = idx === 0 ? "border-l-2 border-l-amber-400" : "";
  const rCls = idx === lastIdx ? "border-r-2 border-r-amber-400" : "";
  let rounded = "";
  if (pos === "top" && idx === 0) rounded = "rounded-tl-lg";
  else if (pos === "top" && idx === lastIdx) rounded = "rounded-tr-lg";
  else if (pos === "bottom" && idx === 0) rounded = "rounded-bl-lg";
  else if (pos === "bottom" && idx === lastIdx) rounded = "rounded-br-lg";
  return `${tCls} ${bCls} ${lCls} ${rCls} ${rounded}`;
}

/**
 * /mis-vuelos — same table layout as the legacy admin Vuelos page so the
 * UX is consistent. The user's own rows are highlighted via a yellow
 * background on the "Solicita" cell. Past flights and cancelled flights
 * hide by default and reveal via toggles.
 */
export function MisVuelos() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [vuelos, setVuelos] = useState<VueloRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  // Ferries default ON so the user view matches the admin view, where
  // ferry legs are shown by default (the toggle still lets them hide).
  const [showFerries, setShowFerries] = useState(true);
  const [editing, setEditing] = useState<VueloRow | null>(null);
  // Pilot-only filter: "all" | "requester" | "pilot". Lets pilots
  // narrow the main calendar to their own flights without losing the
  // pilot section above.
  const [pilotScope, setPilotScope] = useState<
    "all" | "requester" | "pilot"
  >("all");
  // Aircraft filter: null = show every aircraft (default), otherwise the
  // aircraftId to restrict the list to (e.g. only Cirrus or only Pilatus).
  const [aircraftFilter, setAircraftFilter] = useState<string | null>(null);

  const { rows: aviones } = useCollection<AvionDoc>("Aviones");
  const { rows: aeropuertos } = useCollection<AeropuertoDoc>("Aeropuertos");
  const { rows: distancias } = useCollection<DistanciaDoc>("Distancias");

  const homeBaseByAircraft = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of aviones ?? []) map.set(a.id, a.homeBaseIcao);
    return map;
  }, [aviones]);
  const speedByAircraft = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of aviones ?? []) map.set(a.id, a.speedKt);
    return map;
  }, [aviones]);
  const distanceByPair = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of distancias ?? []) map.set(d.id, d.distanceNm);
    return map;
  }, [distancias]);
  const aeropuertoLabelByIcao = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of aeropuertos ?? []) {
      const shown = a.id.replace(/^_/, "");
      map.set(a.id, `${a.displayName} (${shown})`);
    }
    return map;
  }, [aeropuertos]);

  useEffect(() => {
    // Single-field server query — secondary sort by hora happens client
    // side so we don't depend on the composite index.
    const q = query(collection(db, "Vuelos"), orderBy("fecha"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: VueloRow[] = [];
        snap.forEach((d) =>
          list.push({ id: d.id, ...(d.data() as VueloDoc) })
        );
        list.sort((a, b) => {
          if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
          return a.hora < b.hora ? -1 : a.hora > b.hora ? 1 : 0;
        });
        setVuelos(list);
      },
      (err) => setError(err.message)
    );
    return () => unsub();
  }, []);

  // Mirror the admin Resumen blackout subscription: one onSnapshot per
  // aircraft, filtered to endAt >= today so past windows don't clutter
  // the user view. Flattens into a single list sorted by startAt.
  const [blackouts, setBlackouts] = useState<BlackoutRow[]>([]);
  useEffect(() => {
    if (!aviones || aviones.length === 0) return;
    const cutoff = Timestamp.fromDate(
      new Date(`${todaySantiago()}T00:00:00`)
    );
    const byAircraft = new Map<string, BlackoutRow[]>();
    const unsubs: (() => void)[] = [];
    for (const a of aviones) {
      const q = query(
        collection(db, "Aviones", a.id, "Blackouts"),
        where("endAt", ">=", cutoff),
        orderBy("endAt", "asc")
      );
      const u = onSnapshot(q, (snap) => {
        const list: BlackoutRow[] = [];
        snap.forEach((d) =>
          list.push({
            id: d.id,
            aircraftId: a.id,
            aircraftDisplayName: a.displayName,
            ...(d.data() as BlackoutDoc),
          })
        );
        byAircraft.set(a.id, list);
        const flat = ([] as BlackoutRow[])
          .concat(...byAircraft.values())
          .sort((x, y) => x.startAt.toMillis() - y.startAt.toMillis());
        setBlackouts(flat);
      });
      unsubs.push(u);
    }
    return () => {
      for (const u of unsubs) u();
    };
  }, [aviones]);

  if (error) {
    return (
      <p className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
        {error}
      </p>
    );
  }
  if (vuelos === null) {
    return <p className="text-sm text-slate-500">Cargando vuelos…</p>;
  }

  const today = todaySantiago();
  const isPilot = !!usuario?.roles?.includes("pilot");
  const visibleBase = vuelos.filter((f) => {
    const isCancelled = f.status === "cancelled" || f.status === "rejected";
    const isPast = f.fecha < today;
    // A flight that is both past AND cancelled is revealed by either pill —
    // pressing either one should not leave the user staring at an empty list.
    if (!isPast && !isCancelled) {
      // normal upcoming flight: always show
    } else if (isPast && !isCancelled) {
      if (!showPast) return false;
    } else if (!isPast && isCancelled) {
      if (!showCancelled) return false;
    } else {
      // past AND cancelled: show if either toggle is on
      if (!showPast && !showCancelled) return false;
    }
    if (isPilot && pilotScope !== "all" && usuario) {
      if (pilotScope === "requester" && f.requesterUid !== usuario.id) {
        return false;
      }
      if (
        pilotScope === "pilot" &&
        (f.pilotUid ?? f.pilotUidRequested) !== usuario.id
      ) {
        return false;
      }
    }
    return true;
  });

  // Aircraft filter pills. Counts come off visibleBase (status + pilot
  // scope already applied) so each pill shows how many flights it would
  // reveal — independent of which aircraft is currently selected. The
  // final `visible` list applies the selected aircraft on top.
  const countByAircraft = new Map<string, number>();
  for (const f of visibleBase) {
    countByAircraft.set(f.aircraftId, (countByAircraft.get(f.aircraftId) ?? 0) + 1);
  }
  const aircraftOptions = (aviones ?? [])
    .filter((a) => a.active || countByAircraft.has(a.id))
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const visible = visibleBase.filter(
    (f) => aircraftFilter === null || f.aircraftId === aircraftFilter
  );

  // Pilot users get a top section listing flights assigned to them
  // (or requested with them as the pilot, while still pending), so
  // they can see their own schedule at a glance without having to
  // scan the full calendar below. Plain const (not useMemo) so it
  // doesn't add a hook past the early-return guards above.
  const myPilotFlights: VueloRow[] =
    usuario && isPilot
      ? vuelos
          .filter(
            (f) =>
              f.fecha >= today &&
              f.status !== "cancelled" &&
              f.status !== "rejected" &&
              ((f.pilotUid ?? f.pilotUidRequested) === usuario.id)
          )
          .sort((a, b) => {
            if (a.fecha !== b.fecha) return a.fecha < b.fecha ? -1 : 1;
            return a.hora < b.hora ? -1 : a.hora > b.hora ? 1 : 0;
          })
      : [];
  const totalPast = vuelos.filter((f) => f.fecha < today).length;
  const totalCancelled = vuelos.filter(
    (f) => f.status === "cancelled" || f.status === "rejected",
  ).length;

  // Lookups for the return-flight linkage. `returnByOriginalId` lets a
  // flight know whether a return points back at it (used by the cancel
  // button to offer cascading). `flightById` is for resolving the
  // "Retorno de vuelo X" parent reference.
  const returnByOriginalId = new Map<string, VueloRow>();
  const flightById = new Map<string, VueloRow>();
  for (const v of vuelos) {
    flightById.set(v.id, v);
    if (v.returnOfFlightId) returnByOriginalId.set(v.returnOfFlightId, v);
  }

  // Compute ferries unconditionally for every approved flight so the
  // "Ferries (N)" pill count reflects the true total — independent of
  // whether the toggle is on. We strip them from the render blocks
  // below when the toggle is off.
  const allBlocks: FlightBlock[] = visible.map((f) => {
    const isApprovedish = f.status === "approved" || f.status === "completed";
    if (!isApprovedish) {
      return { flight: f, preFerry: null, postFerry: null };
    }
    const homeBase = homeBaseByAircraft.get(f.aircraftId) ?? null;
    const speed = speedByAircraft.get(f.aircraftId) ?? null;
    let preFerry: FerryLeg | null = null;
    const prevLoc = aircraftLocationBefore(f, vuelos, homeBase);
    if (prevLoc && prevLoc !== f.originIcao) {
      const dist = distanceByPair.get(distanciaId(prevLoc, f.originIcao)) ?? null;
      const ferryMin = computeFerryMin(dist, speed);
      const offsetMin = -(FERRY_GROUND_BUFFER_MIN + ferryMin);
      preFerry = {
        direction: "pre",
        fromIcao: prevLoc,
        toIcao: f.originIcao,
        approxHora: addMinutesHora(f.hora, offsetMin),
        distanceNm: dist,
        flightTimeMin: ferryMin,
      };
    }
    let postFerry: FerryLeg | null = null;
    let toIcao: string | null = null;
    if (f.postFlightDisposition === "home") toIcao = homeBase;
    else if (f.postFlightDisposition === "relocate_other") {
      toIcao = f.postFlightDestinationIcao;
    }
    if (toIcao && toIcao !== f.destinationIcao) {
      const arrivalOffset = (f.flightTimeMin ?? 60) + FERRY_GROUND_BUFFER_MIN;
      const dist = distanceByPair.get(distanciaId(f.destinationIcao, toIcao)) ?? null;
      const ferryMin = computeFerryMin(dist, speed);
      postFerry = {
        direction: "post",
        fromIcao: f.destinationIcao,
        toIcao,
        approxHora: addMinutesHora(f.hora, arrivalOffset),
        distanceNm: dist,
        flightTimeMin: ferryMin,
      };
    }
    return { flight: f, preFerry, postFerry };
  });
  const totalFerryRows = allBlocks.reduce(
    (n, b) => n + (b.preFerry ? 1 : 0) + (b.postFerry ? 1 : 0),
    0
  );
  // Render blocks: drop ferries when the toggle is off so existing
  // `block.preFerry ? ...` checks at every render site keep working
  // without needing an additional `&& showFerries` everywhere.
  const blocks: FlightBlock[] = showFerries
    ? allBlocks
    : allBlocks.map((b) => ({ ...b, preFerry: null, postFerry: null }));

  return (
    <div className="space-y-5">
      <header>
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <h1 className="text-2xl font-semibold tracking-tight text-brand-900">Vuelos</h1>
        <p className="mt-1 text-sm text-slate-600">Calendario completo de vuelos.</p>
        <Link
          to="/solicitar"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-900"
        >
          Solicitar nuevo vuelo →
        </Link>
      </header>

      {blackouts.length > 0 ? <BlackoutsBanner blackouts={blackouts} /> : null}

      {isPilot && myPilotFlights.length > 0 ? (
        <MyPilotFlightsSection
          flights={myPilotFlights}
          navigate={navigate}
        />
      ) : null}

      {isPilot ? (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Filtrar:</span>
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300 text-xs">
            {(
              [
                ["all", "Todos"],
                ["requester", "Como solicitante"],
                ["pilot", "Como piloto"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setPilotScope(value)}
                className={
                  pilotScope === value
                    ? "bg-brand-700 px-3 py-1 font-medium text-white"
                    : "bg-white px-3 py-1 text-slate-700 hover:bg-brand-50"
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {aircraftOptions.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-500">Avión:</span>
          <AircraftPill
            label="Todos"
            count={visibleBase.length}
            active={aircraftFilter === null}
            onClick={() => setAircraftFilter(null)}
          />
          {aircraftOptions.map((a) => (
            <AircraftPill
              key={a.id}
              label={a.displayName}
              count={countByAircraft.get(a.id) ?? 0}
              active={aircraftFilter === a.id}
              onClick={() => setAircraftFilter(a.id)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-slate-500">Mostrar:</span>
        <FilterPill
          label="Pasados"
          count={totalPast}
          on={showPast}
          onChange={setShowPast}
        />
        <FilterPill
          label="Cancelados"
          count={totalCancelled}
          on={showCancelled}
          onChange={setShowCancelled}
        />
        <FilterPill
          label="Ferries"
          count={totalFerryRows}
          on={showFerries}
          onChange={setShowFerries}
        />
      </div>

      {/* Mobile: card stack. Table only renders on md+. */}
      <div className="md:hidden">
        <UserFlightCardsMobile
          blocks={blocks}
          today={today}
          usuario={usuario}
          aeropuertoLabelByIcao={aeropuertoLabelByIcao}
          returnByOriginalId={returnByOriginalId}
          flightById={flightById}
          navigate={navigate}
        />
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="border-b border-slate-200 px-3 py-2">Estado</th>
              <th className="border-b border-slate-200 px-3 py-2">Fecha / Hora</th>
              <th className="border-b border-slate-200 px-3 py-2">Avión</th>
              <th className="border-b border-slate-200 px-3 py-2">Ruta</th>
              <th className="border-b border-slate-200 px-3 py-2 text-center">Pax</th>
              <th className="border-b border-slate-200 px-3 py-2">Solicita</th>
              <th className="border-b border-slate-200 px-3 py-2">Piloto</th>
              <th className="min-w-[16rem] border-b border-slate-200 px-3 py-2">Notas</th>
            </tr>
          </thead>
          <tbody>
            {blocks.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-8 text-center text-sm text-slate-500"
                >
                  No hay vuelos en este filtro.
                </td>
              </tr>
            ) : (
              blocks.flatMap((block, blockIdx) => {
                const f = block.flight;
                const lastCol = 7;
                const hasPre = block.preFerry != null;
                const hasPost = block.postFerry != null;
                const flightPos: "alone" | "top" | "middle" | "bottom" =
                  !hasPre && !hasPost
                    ? "alone"
                    : hasPre && hasPost
                      ? "middle"
                      : hasPre
                        ? "bottom"
                        : "top";

                const renderFerryRow = (
                  leg: FerryLeg,
                  pos: "top" | "bottom",
                  keyStr: string
                ) => {
                  const fromLabel =
                    aeropuertoLabelByIcao.get(leg.fromIcao ?? "") ??
                    leg.fromIcao ??
                    "?";
                  const toLabel =
                    aeropuertoLabelByIcao.get(leg.toIcao ?? "") ??
                    leg.toIcao ??
                    "?";
                  const note =
                    leg.direction === "post"
                      ? `ferry para vuelo a ${f.destinationIcao.replace(/^_/, "")}`
                      : `ferry para vuelo desde ${f.originIcao.replace(/^_/, "")}`;
                  // Muted amber when the parent is past / cancelled
                  // so ferries don't stand out inside grey rows.
                  const ferryIsPast = f.fecha < today;
                  const ferryIsCancelled =
                    f.status === "cancelled" || f.status === "rejected";
                  const ferryRowCls = ferryIsCancelled
                    ? "ferry-cancelled hover:brightness-95 text-slate-500"
                    : ferryIsPast
                      ? "ferry-past hover:brightness-95 text-slate-500"
                      : "bg-teal-50 hover:bg-teal-100";
                  return (
                    <tr key={keyStr} className={ferryRowCls}>
                      <td className={`px-3 py-1.5 ${frameBorders(pos, 0, lastCol)}`}>
                        <StatusBadge status="ferry" />
                      </td>
                      <td className={`whitespace-nowrap px-3 py-1.5 font-mono text-xs text-slate-500 ${frameBorders(pos, 1, lastCol)}`}>
                        <div>{formatFecha(f.fecha)}</div>
                        <div>{leg.approxHora ? `~${leg.approxHora}` : "~?"}</div>
                        <div className="font-sans text-[10px]">
                          {leg.distanceNm != null
                            ? `${Math.round(leg.distanceNm)} NM · `
                            : ""}
                          {leg.flightTimeMin ?? "?"} min
                        </div>
                      </td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-slate-900 ${frameBorders(pos, 2, lastCol)}`}>
                        {f.aircraftDisplayName}
                      </td>
                      <td className={`px-3 py-1.5 text-slate-900 ${frameBorders(pos, 3, lastCol)}`}>
                        <div className="truncate">{fromLabel}</div>
                        <div className="truncate text-slate-500">→ {toLabel}</div>
                      </td>
                      <td className={`px-3 py-1.5 text-center text-slate-400 ${frameBorders(pos, 4, lastCol)}`}>
                        —
                      </td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-slate-400 italic ${frameBorders(pos, 5, lastCol)}`}>
                        automático
                      </td>
                      <td className={`whitespace-nowrap px-3 py-1.5 text-slate-700 ${frameBorders(pos, 6, lastCol)}`}>
                        {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                      </td>
                      <td className={`min-w-[16rem] px-3 py-1.5 text-xs italic text-slate-500 ${frameBorders(pos, 7, lastCol)}`}>
                        {note}
                      </td>
                    </tr>
                  );
                };

                const renderedRows: React.ReactNode[] = [];
                if (block.preFerry) {
                  renderedRows.push(
                    renderFerryRow(block.preFerry, "top", `${f.id}-pre`)
                  );
                }
                const mine =
                  usuario != null &&
                  (f.requesterUid === usuario.id ||
                    (f.requesterEmail &&
                      usuario.email &&
                      f.requesterEmail.toLowerCase() ===
                        usuario.email.toLowerCase()));
                const isPast = f.fecha < today;
                const isCancelled =
                  f.status === "cancelled" || f.status === "rejected";
                const isPending = f.status === "pending";
                let rowCls = "bg-white hover:bg-slate-50";
                if (isCancelled) rowCls = "bg-rose-50 text-slate-600 hover:bg-rose-100";
                else if (isPast) rowCls = "flight-past text-slate-600 hover:brightness-95";
                else if (isPending) rowCls = "flight-pending hover:brightness-95";
                renderedRows.push(
                  <tr
                    key={f.id}
                    onClick={() => navigate(`/admin/flights/${f.id}`)}
                    className={`${rowCls} cursor-pointer`}
                  >
                    <td className={`px-3 py-2 ${frameBorders(flightPos, 0, lastCol)}`}>
                      <StatusBadge status={f.status} />
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700 ${frameBorders(flightPos, 1, lastCol)}`}>
                      <div>
                        {formatFecha(f.fecha)}{" "}
                        <span className="text-slate-400">
                          ({dowEs(f.fecha)})
                        </span>
                      </div>
                      <div className="text-slate-500">
                        {formatHora(f.hora)}
                      </div>
                      {f.distanceNm != null || f.flightTimeMin != null ? (
                        <div className="font-sans text-[10px] text-slate-500">
                          {f.distanceNm != null
                            ? `${Math.round(f.distanceNm)} NM · `
                            : ""}
                          {f.flightTimeMin ?? "?"} min
                        </div>
                      ) : null}
                      {f.returnOfFlightId &&
                      flightById.get(f.returnOfFlightId) ? (
                        <div
                          className="text-[10px] text-slate-500"
                          title="Vuelo de retorno"
                        >
                          ↩ es retorno del{" "}
                          {formatFechaShort(
                            flightById.get(f.returnOfFlightId)!.fecha
                          )}
                        </div>
                      ) : null}
                      {returnByOriginalId.get(f.id) ? (
                        <div
                          className="text-[10px] text-slate-500"
                          title="Tiene un vuelo de retorno"
                        >
                          ↪ con retorno el{" "}
                          {formatFechaShort(
                            returnByOriginalId.get(f.id)!.fecha
                          )}
                        </div>
                      ) : null}
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 text-slate-900 ${frameBorders(flightPos, 2, lastCol)}`}>
                      {f.aircraftDisplayName}
                    </td>
                    <td className={`px-3 py-2 text-slate-900 ${frameBorders(flightPos, 3, lastCol)}`}>
                      <div className="truncate">{f.originDisplay}</div>
                      <div className="truncate text-slate-500">
                        → {f.destinationDisplay}
                      </div>
                    </td>
                    <td className={`px-3 py-2 text-center font-mono text-slate-900 ${frameBorders(flightPos, 4, lastCol)}`}>
                      {f.numPax}
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 ${frameBorders(flightPos, 5, lastCol)}`}>
                      {mine ? (
                        <span className="rounded bg-yellow-200 px-1 font-medium text-slate-900">
                          {f.requesterDisplayName}
                        </span>
                      ) : (
                        <span className="text-slate-700">
                          {f.requesterDisplayName}
                        </span>
                      )}
                    </td>
                    <td className={`whitespace-nowrap px-3 py-2 text-slate-700 ${frameBorders(flightPos, 6, lastCol)}`}>
                      {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                    </td>
                    <td className={`min-w-[16rem] whitespace-pre-wrap break-words px-3 py-2 text-xs text-slate-600 ${frameBorders(flightPos, 7, lastCol)}`}>
                      {f.notes ?? ""}
                    </td>
                  </tr>
                );
                if (block.postFerry) {
                  renderedRows.push(
                    renderFerryRow(block.postFerry, "bottom", `${f.id}-post`)
                  );
                }
                if (blockIdx < blocks.length - 1) {
                  renderedRows.push(
                    <tr key={`${f.id}-spacer`} aria-hidden="true">
                      <td colSpan={8} className="h-1" />
                    </tr>
                  );
                }
                return renderedRows;
              })
            )}
          </tbody>
        </table>
      </div>

      {editing ? (
        <EditFlightModal flight={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

export function EditFlightModal({
  flight,
  onClose,
}: {
  flight: VueloRow;
  onClose: () => void;
}) {
  const { rows: aircraft } = useCollection<AvionDoc>("Aviones", orderBy("displayName"));
  const { rows: accounts } = useCollection<CuentaDoc>("Cuentas", orderBy("code"));
  const { rows: aeropuertos } = useCollection<AeropuertoDoc>(
    "Aeropuertos",
    orderBy("displayCombined")
  );
  const { rows: usuarios } = useCollection<UsuarioDoc>("Usuarios", orderBy("displayName"));
  const [viewAsUser] = useViewAsUser();
  const { usuario } = useAuth();
  // Mirrors the backend split in `updateFlight`: an admin acting as
  // themselves (not in Vista usuario) editing an approved flight runs
  // the admin-edit path — no revert, confirmation email to requester.
  // Everything else runs the user-mod path (revert to pending →
  // re-approval). Drives the subtitle copy and the Mensaje label below.
  const isAdminEditing =
    flight.status === "approved" &&
    !viewAsUser &&
    !!usuario &&
    (usuario.isAdmin || usuario.isSuperAdmin);

  const aircraftOptions = useMemo(() => (aircraft ?? []).filter((a) => a.active), [aircraft]);
  const accountOptions = useMemo(() => (accounts ?? []).filter((a) => a.active), [accounts]);
  const aeropuertoOptions = useMemo(
    () =>
      (aeropuertos ?? [])
        .filter((a) => a.active)
        .map((a) => ({
          icao: a.id,
          displayName: a.displayName,
          city: a.city ?? null,
        })),
    [aeropuertos]
  );
  const pilotOptions = useMemo(
    () => (usuarios ?? []).filter((u) => u.roles?.includes("pilot")),
    [usuarios]
  );

  const [aircraftId, setAircraftId] = useState(flight.aircraftId);
  const [accountCode, setAccountCode] = useState(flight.accountCode ?? "");
  const [fecha, setFecha] = useState(flight.fecha);
  const [hora, setHora] = useState(flight.hora);
  const [originIcao, setOriginIcao] = useState<string | null>(flight.originIcao);
  const [destinationIcao, setDestinationIcao] = useState<string | null>(
    flight.destinationIcao
  );
  const [numPax, setNumPax] = useState<number>(flight.numPax);
  const [pilotUid, setPilotUid] = useState(
    flight.pilotUid ?? flight.pilotUidRequested ?? ""
  );
  const [notes, setNotes] = useState(flight.notes ?? "");
  const [disposition, setDisposition] = useState<string>(
    flight.postFlightDisposition ?? "home"
  );
  const [postDestIcao, setPostDestIcao] = useState<string>(
    flight.postFlightDestinationIcao ?? ""
  );
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);

  const maxPax = aircraftOptions.find((a) => a.id === aircraftId)?.maxPax ?? 9;
  useEffect(() => {
    if (numPax > maxPax) setNumPax(maxPax);
  }, [maxPax, numPax]);
  // When destination equals the aircraft's base, the post-flight ferry
  // decision is moot — hide the control and force "home".
  const homeBaseIcao =
    aircraftOptions.find((a) => a.id === aircraftId)?.homeBaseIcao ?? null;
  const destinationIsBase = !!homeBaseIcao && destinationIcao === homeBaseIcao;
  useEffect(() => {
    if (destinationIsBase && disposition !== "home") setDisposition("home");
  }, [destinationIsBase, disposition]);

  async function save() {
    setError(null);
    if (!fecha) return setError("Selecciona fecha.");
    if (!hora) return setError("Selecciona hora.");
    if (!originIcao) return setError("Selecciona origen.");
    if (!destinationIcao) return setError("Selecciona destino.");
    // Origen == destino is allowed (local sightseeing flight).
    if (!destinationIsBase && disposition === "relocate_other" && !postDestIcao) {
      return setError("Indica el aeropuerto de reposicionamiento.");
    }
    // Admin-edit cool-off: an admin save on an already-approved flight
    // re-sends the confirmation email. If the flight was just saved
    // (<30s ago) admin probably already fired a confirmation, so
    // prompt before a second one fans out.
    const recentMs = 30 * 1000;
    const lastSaveMs = flight.updatedAt
      ? flight.updatedAt.toMillis()
      : 0;
    if (
      isAdminEditing &&
      lastSaveMs &&
      Date.now() - lastSaveMs < recentMs
    ) {
      const secs = Math.max(
        1,
        Math.round((recentMs - (Date.now() - lastSaveMs)) / 1000)
      );
      const ok = window.confirm(
        `Acabas de enviar una confirmación (hace <${secs}s). ¿Enviar otra?`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const call = httpsCallable(functions, "updateFlight");
      await call({
        flightId: flight.id,
        aircraftId,
        accountCode: accountCode || null,
        fecha,
        hora,
        originIcao,
        destinationIcao,
        numPax,
        notes: notes.trim() || null,
        pilotUid: pilotUid || null,
        viewAsUser,
        message: message.trim() || null,
        disposition: destinationIsBase ? "home" : disposition,
        postFlightDestinationIcao:
          !destinationIsBase && disposition === "relocate_other"
            ? postDestIcao
            : null,
      });
      onClose();
    } catch (err) {
      const e = err as {
        code?: string;
        message?: string;
        details?: ConflictDetails;
      };
      if (
        e.code === "functions/failed-precondition" &&
        e.details &&
        (e.details as ConflictDetails).kind === "flight-conflict"
      ) {
        setConflict(e.details as ConflictDetails);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setSaving(false);
    }
  }

  const wasApproved = flight.status === "approved";
  const isLoading = !aircraft || !accounts || !aeropuertos || !usuarios;

  // Mirrors the admin approval form: action button label flips between
  // a no-op label and an action label as soon as any field differs from
  // what the flight currently has. Save is disabled when nothing has
  // changed so the user can't accidentally re-pending an approved flight.
  const isDirty =
    aircraftId !== flight.aircraftId
    || (accountCode || null) !== (flight.accountCode ?? null)
    || fecha !== flight.fecha
    || hora !== flight.hora
    || originIcao !== flight.originIcao
    || destinationIcao !== flight.destinationIcao
    || numPax !== flight.numPax
    || (notes.trim() || null) !== (flight.notes ?? null)
    || (pilotUid || null) !== (flight.pilotUid ?? flight.pilotUidRequested ?? null)
    || (destinationIsBase ? "home" : disposition) !== (flight.postFlightDisposition ?? "home")
    || (!destinationIsBase && disposition === "relocate_other"
        ? postDestIcao
        : null) !== (flight.postFlightDestinationIcao ?? null);
  const saveLabel = saving
    ? "Guardando…"
    : isDirty
      ? (wasApproved ? "Solicitar cambios" : "Guardar cambios")
      : "Guardar";

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <SavingOverlay open={saving} label="Guardando…" />
      <div
        className="my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="text-base font-semibold text-slate-900">
            Editar vuelo {flight.publicNumber}
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            {isAdminEditing
              ? "Los cambios se guardan directo; se envía email de confirmación al solicitante."
              : wasApproved
                ? "Al modificar, vuelo será revisado y aprobado nuevamente."
                : "Tus cambios reemplazan la solicitud actual."}
          </p>
        </header>

        {isLoading ? (
          <p className="mt-6 text-sm text-slate-500">Cargando opciones…</p>
        ) : (
          <div className="mt-4 space-y-4">
            {wasApproved && !isAdminEditing ? (
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Mensaje al admin (opcional)
                </label>
                <ClearableField
                  value={message}
                  onClear={() => setMessage("")}
                  textarea
                >
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Explica brevemente la modificación para que el admin la apruebe más rápido."
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                  />
                </ClearableField>
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <ModalSelect
                label="Cuenta"
                value={accountCode}
                onChange={setAccountCode}
                options={accountOptions.map((a) => ({
                  value: a.code,
                  label: `${a.code} — ${a.displayName}`,
                }))}
              />
              <ModalSelect
                label="Avión"
                value={aircraftId}
                onChange={setAircraftId}
                options={aircraftOptions.map((a) => ({
                  value: a.id,
                  label: a.displayName,
                }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Fecha de vuelo
                </label>
                <DateField
                  value={fecha}
                  onChange={setFecha}
                  min={todaySantiago()}
                  className="mt-1"
                />
              </div>
              <ModalSelect
                label="Hora de despegue"
                value={hora}
                onChange={setHora}
                options={generateTimeGrid().map((t) => ({ value: t, label: t }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700">Origen</label>
                <div className="mt-1">
                  <LocationCombobox
                    value={originIcao}
                    onChange={setOriginIcao}
                    options={aeropuertoOptions}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Destino</label>
                <div className="mt-1">
                  <LocationCombobox
                    value={destinationIcao}
                    onChange={setDestinationIcao}
                    options={aeropuertoOptions}
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <WideSelect
                  label="Número de pasajeros"
                  value={String(numPax)}
                  onChange={(v) => setNumPax(Number(v))}
                  options={Array.from({ length: maxPax + 1 }, (_, i) => ({
                    value: String(i),
                    label: String(i),
                  }))}
                />
                <p className="mt-1 text-xs text-slate-500">Máximo {maxPax}.</p>
              </div>
              <ModalSelect
                label="Piloto"
                value={pilotUid}
                onChange={setPilotUid}
                options={[
                  { value: "", label: "Sin preferencia" },
                  ...pilotOptions.map((p) => ({
                    value: p.id,
                    label: p.displayName ?? p.email ?? p.id,
                  })),
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Notas</label>
              <ClearableField value={notes} onClear={() => setNotes("")} textarea>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                />
              </ClearableField>
            </div>

            {/* Hidden only when the destination is the base (nothing to
                decide). Admins editing an approved flight can change it
                here too: updateFlight persists it, rewrites the ferries
                and sends the "Cambio de Vuelo Aprobado" email. */}
            {!destinationIsBase ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <ModalSelect
                  label="Al terminar (disposición del avión)"
                  value={disposition}
                  onChange={setDisposition}
                  options={[
                    {
                      value: "home",
                      label: `Vuelve a la base${
                        homeBaseIcao ? ` (${homeBaseIcao.replace(/^_/, "")})` : ""
                      }`,
                    },
                    { value: "stay", label: "Se queda en destino" },
                    { value: "relocate_other", label: "Reposicionar a otro lugar" },
                  ]}
                />
                {disposition === "relocate_other" ? (
                  <div>
                    <label className="block text-sm font-medium text-slate-700">
                      Reposicionar a (ICAO)
                    </label>
                    <input
                      type="text"
                      value={postDestIcao}
                      onChange={(e) => setPostDestIcao(e.target.value.toUpperCase())}
                      placeholder="SCEL"
                      maxLength={4}
                      className="mt-1 block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {wasApproved && isAdminEditing ? (
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Mensaje al usuario (opcional)
                </label>
                <ClearableField
                  value={message}
                  onClear={() => setMessage("")}
                  textarea
                >
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Acompaña la confirmación con un mensaje al solicitante."
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
                  />
                </ClearableField>
              </div>
            ) : null}

            {error ? (
              <p className="rounded border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                {error}
              </p>
            ) : null}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={saving || isLoading || !isDirty}
            onClick={save}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
          >
            {saveLabel}
          </button>
        </div>
      </div>
      {conflict ? (
        <ConflictModal
          conflict={conflict}
          attempt={{
            fecha: formatFecha(fecha),
            hora,
            aircraftDisplayName:
              aircraftOptions.find((a) => a.id === aircraftId)?.displayName ??
              aircraftId,
            origin:
              aeropuertoOptions.find((a) => a.icao === originIcao)
                ?.displayName ?? (originIcao ?? ""),
            destination:
              aeropuertoOptions.find((a) => a.icao === destinationIcao)
                ?.displayName ?? (destinationIcao ?? ""),
          }}
          context="editar"
          onClose={() => setConflict(null)}
        />
      ) : null}
    </div>
  );
}

function BlackoutsBanner({ blackouts }: { blackouts: BlackoutRow[] }) {
  const nowMs = Date.now();
  return (
    <section className="rounded-lg border-2 border-orange-300 bg-orange-50 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-orange-900">
          Bloqueos de aviones
        </h2>
        <span className="rounded-full bg-orange-200 px-2 py-0.5 text-[11px] font-medium text-orange-900">
          {blackouts.length}
        </span>
      </div>
      <ul className="mt-2 grid grid-cols-[max-content_max-content_max-content_max-content_1fr] gap-x-3 gap-y-1 text-xs">
        {blackouts.map((b) => {
          const active =
            b.startAt.toMillis() <= nowMs && b.endAt.toMillis() >= nowMs;
          return (
            <li key={b.id} className="contents text-slate-700">
              <span
                className={
                  active
                    ? "font-semibold text-orange-900"
                    : "font-medium text-orange-900"
                }
              >
                {b.aircraftDisplayName}
              </span>
              <span className="whitespace-nowrap text-right">
                {formatFecha(tsToIso(b.startAt))}
              </span>
              <span className="text-slate-500">→</span>
              <span className="whitespace-nowrap">
                {formatFecha(tsToIso(b.endAt))}
              </span>
              <span className="flex flex-wrap items-baseline gap-x-2 text-slate-600">
                {b.reason ?? <span className="text-slate-400">—</span>}
                {active ? (
                  <span className="rounded bg-orange-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-orange-900">
                    activo
                  </span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function MyPilotFlightsSection({
  flights,
  navigate,
}: {
  flights: VueloRow[];
  navigate: (to: string) => void;
}) {
  return (
    <section className="rounded-lg border border-sky-200 bg-sky-50/60 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-sky-900">
          Mis vuelos como piloto
        </h2>
        <span className="font-mono text-xs text-sky-900/70">
          {flights.length}
        </span>
      </div>
      <ul className="mt-3 space-y-2">
        {flights.map((f) => (
          <li
            key={f.id}
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/admin/flights/${f.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter") navigate(`/admin/flights/${f.id}`);
            }}
            className="block cursor-pointer rounded-md border border-sky-100 bg-white px-3 py-2 text-sm hover:bg-sky-50"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-xs text-slate-700">
                {formatFecha(f.fecha)}{" "}
                <span className="text-slate-400">({dowEs(f.fecha)})</span>
                {" · "}
                {formatHora(f.hora)}
              </span>
              <StatusBadge status={f.status} />
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {f.aircraftDisplayName}
            </div>
            <div className="truncate text-xs text-slate-700">
              {f.originDisplay}{" "}
              <span className="whitespace-nowrap text-slate-500">→ {f.destinationDisplay}</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-600">
              <span className="text-slate-500">Solicita:</span>{" "}
              {f.requesterDisplayName}
              {f.accountCode ? (
                <>
                  {" · "}
                  <span className="text-slate-500">Cuenta</span>{" "}
                  <span className="font-mono">{f.accountCode}</span>
                </>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ModalSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <WideSelect
      label={label}
      value={value}
      onChange={onChange}
      options={options}
    />
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    approved: "border border-emerald-200 bg-emerald-50 text-emerald-800",
    pending: "border border-amber-200 bg-amber-50 text-amber-800",
    rejected: "border border-rose-200 bg-rose-50 text-rose-800",
    cancelled: "border border-rose-200 bg-rose-50 text-rose-800",
    completed: "border border-slate-200 bg-slate-50 text-slate-600",
  };
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
        cls[status] ?? "bg-slate-100 text-slate-700"
      }`}
    >
      {status}
    </span>
  );
}

/**
 * Compact pill-style filter toggle. "+ Label (N)" when off (slate
 * border, hollow); fills with brand-700 + white text when on. Used in
 * the /mis-vuelos header to keep the filter row tight on mobile.
 */
function FilterPill({
  label,
  count,
  on,
  onChange,
}: {
  label: string;
  count: number;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition " +
        (on
          ? "border-brand-700 bg-brand-700 text-white"
          : "border-slate-300 bg-white text-slate-700 hover:bg-brand-50")
      }
    >
      <span>{label}</span>
      <span className={on ? "text-white/70" : "text-slate-400"}>{count}</span>
    </button>
  );
}

/**
 * Single-select pill for the aircraft filter (Todos / Cirrus / Pilatus).
 * Styled to match FilterPill above so the "Avión:" row reads as part of
 * the same control set, but behaves as a radio (one active at a time).
 */
function AircraftPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition " +
        (active
          ? "border-brand-700 bg-brand-700 text-white"
          : "border-slate-300 bg-white text-slate-700 hover:bg-brand-50")
      }
    >
      <span>{label}</span>
      <span className={active ? "text-white/70" : "text-slate-400"}>{count}</span>
    </button>
  );
}

/**
 * Mobile (md-) card layout for the user-facing Vuelos page. publicNumber
 * lives at the bottom in a small font. Tapping the card navigates to
 * the flight detail page where edit / cancel actions live.
 */
function UserFlightCardsMobile({
  blocks,
  today,
  usuario,
  aeropuertoLabelByIcao,
  returnByOriginalId,
  flightById,
  navigate,
}: {
  blocks: FlightBlock[];
  today: string;
  usuario: Usuario | null;
  aeropuertoLabelByIcao: Map<string, string>;
  returnByOriginalId: Map<string, VueloRow>;
  flightById: Map<string, VueloRow>;
  navigate: (to: string) => void;
}) {
  if (blocks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
        No hay vuelos en este filtro.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {blocks.map((block) => {
        const f = block.flight;
        const isPast = f.fecha < today;
        const isCancelled =
          f.status === "cancelled" || f.status === "rejected";
        const isPending = f.status === "pending";
        const hasFerry = !!block.preFerry || !!block.postFerry;
        const wrap = hasFerry
          ? "rounded-lg border-2 border-teal-400 overflow-hidden"
          : "rounded-lg border border-slate-200 overflow-hidden";
        let flightBg = "bg-white";
        if (isCancelled) flightBg = "bg-rose-50";
        else if (isPast) flightBg = "flight-past";
        else if (isPending) flightBg = "flight-pending";
        const mine =
          usuario != null &&
          (f.requesterUid === usuario.id ||
            (f.requesterEmail &&
              usuario.email &&
              f.requesterEmail.toLowerCase() ===
                usuario.email.toLowerCase()));
        return (
          <div key={f.id} className={wrap}>
            {block.preFerry ? (
              <UserFerryCardMobile
                flight={f}
                leg={block.preFerry}
                aeropuertoLabelByIcao={aeropuertoLabelByIcao}
                borderBelow
              />
            ) : null}
            <div
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/admin/flights/${f.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter") navigate(`/admin/flights/${f.id}`);
              }}
              className={`${flightBg} cursor-pointer px-4 py-3 text-sm`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-mono text-xs text-slate-700">
                  <div>
                    {formatFecha(f.fecha)}{" "}
                    <span className="text-slate-400">({dowEs(f.fecha)})</span>{" "}
                    · {formatHora(f.hora)}
                  </div>
                  {f.distanceNm != null || f.flightTimeMin != null ? (
                    <div className="text-[10px] text-slate-500">
                      {f.distanceNm != null
                        ? `${Math.round(f.distanceNm)} NM · `
                        : ""}
                      {f.flightTimeMin ?? "?"} min
                    </div>
                  ) : null}
                </div>
                <StatusBadge status={f.status} />
              </div>
              <div className="mt-1 font-medium text-slate-900">
                {f.aircraftDisplayName}
              </div>
              <div className="mt-1 truncate text-sm text-slate-800">
                {f.originDisplay}{" "}
                <span className="whitespace-nowrap text-slate-500">→ {f.destinationDisplay}</span>
              </div>
              {f.returnOfFlightId &&
              flightById.get(f.returnOfFlightId) ? (
                <div className="mt-1 text-[11px] text-slate-500">
                  ↩ Es retorno del{" "}
                  {formatFechaShort(
                    flightById.get(f.returnOfFlightId)!.fecha
                  )}
                </div>
              ) : null}
              {returnByOriginalId.get(f.id) ? (
                <div className="mt-1 text-[11px] text-slate-500">
                  ↪ Con retorno el{" "}
                  {formatFechaShort(returnByOriginalId.get(f.id)!.fecha)}
                </div>
              ) : null}
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                <div>
                  <span className="text-slate-500">Solicita:</span>{" "}
                  {mine ? (
                    <span className="rounded bg-yellow-200 px-1 font-medium text-slate-900">
                      {f.requesterDisplayName}
                    </span>
                  ) : (
                    f.requesterDisplayName
                  )}
                </div>
                <div>
                  <span className="text-slate-500">Piloto:</span>{" "}
                  {f.pilotDisplayName ?? f.pilotDisplayNameRequested ?? "—"}
                </div>
                <div>
                  <span className="text-slate-500">Pax:</span>{" "}
                  <span className="font-mono">{f.numPax}</span>
                </div>
              </div>
              {f.notes ? (
                <div className="mt-2 whitespace-pre-wrap text-xs text-slate-600">
                  {f.notes}
                </div>
              ) : null}
              <div className="mt-2 text-right text-[10px] font-mono text-slate-400">
                {f.publicNumber}
              </div>
            </div>
            {block.postFerry ? (
              <UserFerryCardMobile
                flight={f}
                leg={block.postFerry}
                aeropuertoLabelByIcao={aeropuertoLabelByIcao}
                borderAbove
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function UserFerryCardMobile({
  flight,
  leg,
  aeropuertoLabelByIcao,
  borderAbove,
  borderBelow,
}: {
  flight: VueloRow;
  leg: FerryLeg;
  aeropuertoLabelByIcao: Map<string, string>;
  borderAbove?: boolean;
  borderBelow?: boolean;
}) {
  const fromLabel =
    aeropuertoLabelByIcao.get(leg.fromIcao ?? "") ?? leg.fromIcao ?? "?";
  const toLabel =
    aeropuertoLabelByIcao.get(leg.toIcao ?? "") ?? leg.toIcao ?? "?";
  const tipo = leg.direction === "post" ? "Ferry posterior" : "Ferry previo";
  const divider = borderAbove
    ? "border-t border-teal-300"
    : borderBelow
      ? "border-b border-teal-300"
      : "";
  const today = todaySantiago();
  const isPast = flight.fecha < today;
  const isCancelled =
    flight.status === "cancelled" || flight.status === "rejected";
  const bgCls = isCancelled
    ? "ferry-cancelled text-slate-500"
    : isPast
      ? "ferry-past text-slate-500"
      : "bg-teal-50 text-slate-700";
  return (
    <div className={`${bgCls} px-4 py-3 text-sm ${divider}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-mono text-xs text-slate-700">
          <div>
            {formatFecha(flight.fecha)}{" "}
            <span className="text-slate-400">({dowEs(flight.fecha)})</span>{" "}
            · {leg.approxHora ? `~${leg.approxHora}` : "~?"}
          </div>
          {leg.distanceNm != null || leg.flightTimeMin != null ? (
            <div className="text-[10px] text-slate-500">
              {leg.distanceNm != null
                ? `${Math.round(leg.distanceNm)} NM · `
                : ""}
              {leg.flightTimeMin ?? "?"} min
            </div>
          ) : null}
        </div>
        <StatusBadge status="ferry" />
      </div>
      <div className="mt-1 font-medium text-slate-900">
        {flight.aircraftDisplayName}
      </div>
      <div className="mt-1 truncate text-sm text-slate-800">
        {fromLabel} <span className="text-slate-500">→ {toLabel}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
        <div className="italic">
          <span className="text-slate-500">Tipo:</span> {tipo}
        </div>
        <div>
          <span className="text-slate-500">Cta:</span>{" "}
          <span className="font-mono">{flight.accountCode ?? "—"}</span>
        </div>
        <div>
          <span className="text-slate-500">Piloto:</span>{" "}
          {flight.pilotDisplayName ?? flight.pilotDisplayNameRequested ?? "—"}
        </div>
      </div>
    </div>
  );
}
